import { paramCase } from './changeCase';
import { isNullOrUndefined, isNullOrEmpty, isObject } from './typeChecks';
import { IComponentStyle } from '../models/componentStyle/IComponentStyle';
import { IBreakpoint } from '../models/componentStyle/IBreakpoint';
import { ISelectors } from '../models/componentStyle/ISelectors';
import { EOL } from 'os';
import { IConditionalStyle } from '../models/componentStyle/IConditionalStyle';
import { ConditionalHelper } from '../helpers/ConditionalHelper';
import { IFontFace } from '../models/componentStyle/IFontFace';
import { isArrayLike } from './isArrayLike';

export function getCss(componentStyle: IComponentStyle | undefined | null, props?: any, existingStyles?: string[], ignorePropsComponentStyle: boolean = false) {
	const response: string[] = existingStyles || [];

	if (componentStyle) {
		const {
			breakpoints,
			selectors,
			conditionals,
		} = componentStyle;
		const exclusionKeys = ['_raw', 'breakpoints', 'selectors', 'conditionals', '_parameters', 'sizes', 'priorities', 'children', 'show', 'overrides', 'arrowDirection', 'chartProps', 'seriesColors', 'selected', 'groupHeader', 'groupChild'];
		// Loop over the available keys in the theme element object and add the styles for those to the response
		Object.entries(componentStyle).map(([key, value]) => {
			if (exclusionKeys.indexOf(key) === -1) {
				addStyleForObjectOrString(response, value, paramCase(key));
			}
		});

		addStylesForConditionals(response, conditionals, props);

		// Break points happen at the end for any overrides
		addStyleForBreakpoints(response, breakpoints, props);

		// ISelectors (hover etc)
		addStyleForSelectors(response, selectors, props);

		// Overrides for child components
		addStyleOverrides(response, componentStyle, props);

		if (componentStyle._raw && !isNullOrEmpty(componentStyle._raw)) {
			response.push(componentStyle._raw);
		}
	}
	if (!ignorePropsComponentStyle && props && props.componentStyle) {
		getCss(props.componentStyle, props, response, true);
	}

	return getCleanStyle(response);
}

export function getCleanStyle(collection: string[]) {
	return collection.filter(value => !isNullOrEmpty(value)).join(EOL);
}

export function addStyleForBreakpoints(collection: string[], breakpoints?: IBreakpoint[], props?: any) {
	if (breakpoints) {
		for (const breakpoint of breakpoints) {
			collection.push(...getStyleForBreakpoint(breakpoint, props));
		}
	}
}

export function getStyleForBreakpoint(breakpoint: IBreakpoint, props?: any) {
	const response: string[] = [];
	const key = !isNullOrUndefined(breakpoint.max) ? 'max' : (!isNullOrUndefined(breakpoint.min) ? 'min' : null);
	if (key) {
		const value = breakpoint[key];
		const defaultMedia = ['print', 'screen'];
		const media = breakpoint.media || defaultMedia;
		response.push(`@media ${media.join(', ')} and (${key}-width: ${value}) {`);
		getCss(breakpoint.style, props, response);
		response.push(`}`);
	}
	return response;
}

export function addStyleForSelectors(collection: string[], selectors?: ISelectors | ISelectors[], props?: any) {
	const selectorsArray: ISelectors[] = [];
	if (!isNullOrUndefined(selectors)) {
		if (Array.isArray(selectors)) {
			selectorsArray.push(...selectors);
		}
		else {
			selectorsArray.push(selectors);
		}
	}

	for (const selector of selectorsArray) {
		Object.entries(selector).map(([key, value]) => {
			let workingKey = key;
			if (value['_parameters'] && Array.isArray(value['_parameters'])) {
				workingKey += `(${value['_parameters'].join(',')})`;
			}
			collection.push(`&${workingKey} {`);
			getCss(value as IComponentStyle, props, collection);
			collection.push(`}`);
		});
	}

}

function addStylesForConditionals(collection: string[], conditionalStyles: IConditionalStyle[] | undefined, props: any) {
	const conditionalCss = (conditionalStyles || []).reduce((acc: string[], conditionalStyle) => {
		if (conditionalStyle) {
			const { conditional, style } = conditionalStyle;
			const conditionalHelper = new ConditionalHelper(conditional);
			if (conditionalHelper.matches(props)) {
				acc.push(getCss(style, props));
			}
		}
		return acc;
	}, []);
	return collection.push(...conditionalCss);
}

export function addStyleOverrides(collection: string[], componentStyle: IComponentStyle | undefined | null, props?: any) {
	if (componentStyle && componentStyle.overrides) {
		for (const override of componentStyle.overrides) {
			const { selector, style } = override;
			const childSelector = ['&', selector].join(' ');
			collection.push(`${childSelector} {`);
			getCss(style, props, collection, true);
			collection.push(`}`);
		}
	}
}

export function getStyleForObject(styleObject: any, prefix: string) {
	const response: string[] = [];
	if (!isNullOrUndefined(styleObject)) {
		const workingPrefix = prefix && prefix.toLowerCase() === prefix ? prefix : paramCase(prefix);
		if (!isNullOrUndefined(styleObject['_value'])) {
			response.push(`${workingPrefix}: ${styleObject['_value']};`);
		}
		Object.entries(styleObject).map(([key, value]) => {
			if (!key.startsWith('_') && !isNullOrUndefined(value) && value !== '') {
				if (isObject(value)) {
					response.push(...getStyleForObject(value, `${workingPrefix}-${paramCase(key)}`));
				}
				else {
					response.push(`${workingPrefix}-${paramCase(key)}: ${value};`);
				}
			}
		});
	}
	return response;
}

export function addStyleForObjectOrString(collection: string[], styleObject: any, prefix: string) {
	const workingPrefix = paramCase(prefix);
	const response: string[] = [];
	if (!isNullOrUndefined(styleObject)) {
		if (isObject(styleObject)) {
			response.push(...getStyleForObject(styleObject, workingPrefix));
		}
		else {
			response.push(`${workingPrefix}: ${styleObject};`);
		}
		collection.push(...response);
	}
	return '';
}

function orchestrateFontFace(fontFace: IFontFace): IFontFace | undefined {
	if (fontFace && fontFace.fontFamily && fontFace.src && fontFace.src.length > 0) {
		return Object.keys(fontFace).sort().reduce((acc, key) => {
			acc[key] = fontFace[key];
			return acc;
		}, {}) as IFontFace;
	}
	return undefined;
}

function getFontFaceId(fontFace: IFontFace): string {
	return JSON.stringify(fontFace);
}

function joinPaths(...paths: string[]) {
	return paths
		.filter(path => typeof path === 'string' && path.length > 0)
		.map((path) => {
			let response = path;
			while (response.startsWith('/')) {
				response = response.substr(1);
			}
			while (response.endsWith('/')) {
				response = response.substr(0, response.length - 1);
			}
			return response;
		})
		.join('/');
}

export function getFontFaceCss(fontFace: IFontFace, baseUrl: string, fontFaceStore: { [key: string]: boolean }) {
	let response: string | undefined = undefined;
	fontFace = orchestrateFontFace(fontFace);
	if (fontFace && fontFace.fontFamily && fontFace.src) {
		const keys = Object.keys(fontFace);
		const fontFaceId = getFontFaceId(fontFace);
		if (!fontFaceStore || !fontFaceStore[fontFaceId]) {
			response = `@font-face {
	${keys.map((key: keyof IFontFace) => {
				let value = fontFace[key];
				if (key === 'fontFamily') {
					value = `"${fontFace[key]}"`;
				}
				if (key === 'src') {
					const src = fontFace[key];
					value = '';
					if (isArrayLike(src)) {
						value = src.map((srcItem) => {
							const { url, format } = srcItem;
							let workingUrl = url;
							if (!['https://', 'http://'].some(prefix => workingUrl && workingUrl.startsWith(prefix))) {
								workingUrl = joinPaths(baseUrl, workingUrl);
							}
							let srcStr = `url("${workingUrl}")`;
							if (format && format.length > 0) {
								srcStr += ` format("${format}")`;
							}
							return srcStr;
						}).join(', ');
					}
				}
				return `${paramCase(key)}: ${value};`;
			}).join('\n	')}
}`;
			fontFaceStore[fontFaceId] = true;
		}
	}
	return response;
}

export function getFontFacesCss(fontFaces: IFontFace[], baseUrl?: string) {
	if (!isArrayLike(fontFaces)) {
		return '';
	}
	const fontFaceStore = getFontFaceStore();
	const results = fontFaces
		.map(fontFace => getFontFaceCss(fontFace, baseUrl, fontFaceStore))
		.filter(result => !!result)
		.join(EOL);
	return results;
}

export function getFontFaceStore(): { [key: string]: boolean } {
	const FONT_FACE_STORE_KEY = '__ksysFontFaceStore__';
	if (!(window as any)[FONT_FACE_STORE_KEY]) {
		(window as any)[FONT_FACE_STORE_KEY] = {};
	}
	return (window as any)[FONT_FACE_STORE_KEY];
}